import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';

import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';

import { EMPTY_MISC_PURCHASE_ORDER_LINE } from '../schemas/miscPurchaseOrderSchema';

/** 品名｜規格｜數量｜單價｜行備註｜移除（對齊物料採購單明細的 list 版型） */
// eslint-disable-next-line max-len -- Tailwind arbitrary grid 須單一完整 class 字面量
const LINE_GRID_COLS = '@xl/form-section:grid-cols-[minmax(10rem,1.4fr)_minmax(8rem,1fr)_minmax(6.5rem,7rem)_minmax(6.5rem,7rem)_minmax(8rem,1fr)_2.5rem]';

const LINES_MIN_W = cn('w-full', '@xl/form-section:min-w-[48rem]');

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 items-start gap-3 px-3 py-3',
  LINE_GRID_COLS,
);

/** 窄螢幕堆疊時才顯示小標；寬螢幕靠表頭 */
const STACK_LABEL_CLASS = '@xl/form-section:hidden';

const MiscPurchaseOrderLinesEditor = ({
  control,
  register,
  errors,
  disabled = false,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lines',
  });
  const linesRootMessage = typeof errors?.lines?.message === 'string'
    ? errors.lines.message
    : null;

  return (
    <section className="@container/form-section">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h2 className="form-block__title mb-0!">明細</h2>
        <Button
          type="button"
          size="sm"
          disabled={disabled}
          onClick={() => append({ ...EMPTY_MISC_PURCHASE_ORDER_LINE })}
        >
          新增明細
        </Button>
      </div>

      {linesRootMessage ? (
        <p className="mb-3 text-sm text-red-500">{linesRootMessage}</p>
      ) : null}

      {/* 外層負責橫向捲動；內層固定最小寬，視窗收縮時整張列表可左右滑動，數量／單價不被壓扁 */}
      <div className="create-edit-panel-scroll w-full max-w-full overflow-x-auto rounded-md border border-gray-300">
        <div className={LINES_MIN_W}>
          <div className={LINE_HEADER_CLASS} aria-hidden>
            <span>品名</span>
            <span>規格</span>
            <span>數量</span>
            <span>單價</span>
            <span>行備註</span>
            <span className="sr-only">操作</span>
          </div>
          <ul className="m-0 list-none divide-y divide-gray-200 p-0">
            {fields.map((field, index) => (
              <li key={field.id} className={LINE_ROW_CLASS}>
                <input type="hidden" {...register(`lines.${index}.lineId`)} />
                <div className="min-w-0">
                  <PlainRegisteredInput
                    id={`lines.${index}.itemName`}
                    register={register}
                    errors={errors}
                    mobileLabel="品名"
                    mobileLabelClassName={STACK_LABEL_CLASS}
                    ariaLabel={`品名 ${index + 1}`}
                    readOnly={disabled}
                  />
                </div>
                <div className="min-w-0">
                  <PlainRegisteredInput
                    id={`lines.${index}.specification`}
                    register={register}
                    errors={errors}
                    mobileLabel="規格"
                    mobileLabelClassName={STACK_LABEL_CLASS}
                    ariaLabel={`規格 ${index + 1}`}
                    readOnly={disabled}
                  />
                </div>
                <div className="min-w-0">
                  <PlainRegisteredInput
                    id={`lines.${index}.qty`}
                    register={register}
                    errors={errors}
                    type="number"
                    min="0"
                    step="any"
                    mobileLabel="數量"
                    mobileLabelClassName={STACK_LABEL_CLASS}
                    ariaLabel={`數量 ${index + 1}`}
                    readOnly={disabled}
                  />
                </div>
                <div className="min-w-0">
                  <PlainRegisteredInput
                    id={`lines.${index}.unitPrice`}
                    register={register}
                    errors={errors}
                    type="number"
                    min="0"
                    step="any"
                    mobileLabel="單價"
                    mobileLabelClassName={STACK_LABEL_CLASS}
                    ariaLabel={`單價 ${index + 1}`}
                    readOnly={disabled}
                  />
                </div>
                <div className="min-w-0">
                  <PlainRegisteredInput
                    id={`lines.${index}.remark`}
                    register={register}
                    errors={errors}
                    mobileLabel="行備註"
                    mobileLabelClassName={STACK_LABEL_CLASS}
                    ariaLabel={`行備註 ${index + 1}`}
                    readOnly={disabled}
                  />
                </div>
                <div className="flex min-w-0 @xl/form-section:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                    aria-label={`移除明細 ${index + 1}`}
                    disabled={disabled || fields.length <= 1}
                    onClick={() => remove(index)}
                  >
                    <PiTrashBold className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

MiscPurchaseOrderLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
};

export default MiscPurchaseOrderLinesEditor;
